import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { resultsService } from '../services/results.service';
import { costService } from '../services/cost.service';
import { handleApiCall, displayError } from '../lib/error-handler';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert, AlertDescription } from '../components/ui/Alert';
import { formatDate, formatCost, formatDuration } from '../lib/utils';
import { ExternalLink, TrendingUp, Filter } from 'lucide-react';
import type { JobResultsResponse, AnalyzedArticle, CostStatsResponse } from '../types/api';
import { Pagination } from '../components/ui/Pagination';

type SortOption = 'score-desc' | 'score-asc' | 'date-desc' | 'date-asc';

export function ResultsPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [results, setResults] = useState<JobResultsResponse | null>(null);
  const [costStats, setCostStats] = useState<CostStatsResponse | null>(null);
  const [filteredArticles, setFilteredArticles] = useState<AnalyzedArticle[]>([]);

  const [minScore, setMinScore] = useState(0);
  const [sortBy, setSortBy] = useState<SortOption>('score-desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    if (jobId) {
      loadResults(jobId);
    }
  }, [jobId]);

  useEffect(() => {
    if (results) {
      filterAndSortArticles();
    }
  }, [results, minScore, sortBy, searchTerm]);

  const loadResults = async (jobId: string) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Loading results for job:', jobId);

      // Fetch job details and articles (required)
      const [jobData, resultsData] = await Promise.all([
        handleApiCall(
          () => resultsService.getJob(jobId),
          'Failed to load job details'
        ),
        handleApiCall(
          () => resultsService.getJobResults(jobId, 0, 10000, 0), // Get all articles (up to 10k), filter client-side
          'Failed to load results'
        )
      ]);

      console.log('Job loaded:', jobData);
      console.log('Results loaded:', resultsData);

      // Combine the data
      const combinedData: JobResultsResponse = {
        ...resultsData,
        job: jobData
      };

      setResults(combinedData);

      // Fetch cost stats separately (optional - don't block page load if it fails)
      try {
        const costData = await costService.getCostStats();
        console.log('Cost stats loaded:', costData);
        setCostStats(costData);
      } catch (costErr) {
        console.warn('Cost stats unavailable:', costErr);
        // Don't show error to user - cost stats are optional
        setCostStats(null);
      }
    } catch (err) {
      console.error('Error loading results:', err);
      setError(displayError(err));
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortArticles = () => {
    if (!results) return;

    console.log('Filtering articles. Total articles:', results.articles.length);
    console.log('minScore:', minScore, 'sortBy:', sortBy, 'searchTerm:', searchTerm);

    let filtered = results.articles.filter(article => {
      // Filter by minimum score
      if (article.relevance_score < minScore) return false;

      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          article.title.toLowerCase().includes(term) ||
          (article.relevance_explanation?.toLowerCase().includes(term) ?? false)
        );
      }

      return true;
    });

    // Sort articles
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'score-desc':
          return b.relevance_score - a.relevance_score;
        case 'score-asc':
          return a.relevance_score - b.relevance_score;
        case 'date-desc':
          // Put articles without dates at the end
          if (!a.published_at && !b.published_at) return 0;
          if (!a.published_at) return 1;
          if (!b.published_at) return -1;
          return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
        case 'date-asc':
          // Put articles without dates at the end
          if (!a.published_at && !b.published_at) return 0;
          if (!a.published_at) return 1;
          if (!b.published_at) return -1;
          return new Date(a.published_at).getTime() - new Date(b.published_at).getTime();
        default:
          return 0;
      }
    });

    console.log('Filtered articles count:', filtered.length);
    setFilteredArticles(filtered);
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'bg-emerald-500';
    if (score >= 60) return 'bg-cyan-500';
    if (score >= 30) return 'bg-amber-500';
    return 'bg-slate-500';
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedArticles = filteredArticles.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [minScore, sortBy, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!results || !results.job) {
    console.log('Results or job is null/undefined:', { results, job: results?.job });
    return null;
  }

  const { job } = results;

  // Calculate relevant articles count (score >= 80)
  const relevantCount = results.articles.filter(article => article.relevance_score >= 80).length;

  // Get monthly cost data
  const monthlyCost = costStats?.total.cost ?? 0;
  const costLimit = costStats?.limit.monthly ?? 0;
  const costRemaining = costStats?.limit.remaining ?? 0;
  const percentUsed = costStats?.limit.percentUsed ?? 0;

  console.log('Rendering results page. Articles to display:', filteredArticles.length);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Analysis Results</h1>
        <p className="text-muted-foreground">
          Job completed on {formatDate(job.completed_at || job.created_at)}
        </p>
      </div>

      {/* Job Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Total Articles</div>
              <div className="text-2xl font-bold">{results.articles.length}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Relevant Articles</div>
              <div className="text-2xl font-bold text-emerald-400">{relevantCount}</div>
              <div className="text-xs text-muted-foreground mt-1">Score ≥ 80</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Duration</div>
              <div className="text-2xl font-bold">{formatDuration(job.duration_seconds)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Monthly Cost</div>
              <div className="text-2xl font-bold">{formatCost(monthlyCost)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatCost(costRemaining)} / {formatCost(costLimit)} remaining
              </div>
              {percentUsed >= 80 && (
                <div className="text-xs text-amber-400 mt-1">
                  {percentUsed.toFixed(0)}% of limit used
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            <CardTitle>Filters</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="min-score">
                Minimum Score: {minScore}
              </Label>
              <Input
                id="min-score"
                type="range"
                min="0"
                max="100"
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sort">Sort By</Label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="score-desc">Score (High to Low)</option>
                <option value="score-asc">Score (Low to High)</option>
                <option value="date-desc">Date (Newest First)</option>
                <option value="date-asc">Date (Oldest First)</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <Input
                id="search"
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredArticles.length} of {results.articles.length} articles
          </div>
        </CardContent>
      </Card>

      {/* Pagination Top */}
      {filteredArticles.length > 0 && (
        <Card className="my-6">
          <CardContent className="py-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              totalItems={filteredArticles.length}
            />
          </CardContent>
        </Card>
      )}

      {/* Articles */}
      <div className="space-y-4">
        {filteredArticles.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">
                No articles found matching your filters.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setMinScore(0);
                  setSearchTerm('');
                }}
                className="mt-4"
              >
                Reset Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          paginatedArticles.map((article) => (
            <Card key={article.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  {/* Score Badge */}
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 rounded-full ${getScoreColor(article.relevance_score)} flex items-center justify-center text-white font-bold text-lg`}>
                      {article.relevance_score}
                    </div>
                  </div>

                  {/* Article Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold mb-2 flex items-start gap-2">
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        {article.title}
                      </a>
                      <ExternalLink className="w-4 h-4 flex-shrink-0 mt-1 text-muted-foreground" />
                    </h3>

                    {article.published_at && (
                      <p className="text-sm text-muted-foreground mb-3">
                        Published: {formatDate(article.published_at)}
                      </p>
                    )}

                    {article.relevance_explanation && (
                      <div className="bg-muted/50 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-2">
                          <TrendingUp className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                          <p className="text-sm">{article.relevance_explanation}</p>
                        </div>
                      </div>
                    )}

                    {article.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {article.excerpt}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination Bottom */}
      {filteredArticles.length > 0 && (
        <Card className="mt-6">
          <CardContent className="py-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              totalItems={filteredArticles.length}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
